import type { MoneyCents } from "./types.js";

export function cents(value: bigint | number | string): MoneyCents {
  if (typeof value === "bigint") return value;

  if (typeof value === "number") {
    if (!Number.isSafeInteger(value)) {
      throw new Error("Money in cents must be a safe integer.");
    }
    return BigInt(value);
  }

  if (!/^-?\d+$/.test(value)) {
    throw new Error("Money string must contain integer cents only.");
  }
  return BigInt(value);
}

export function addMoney(values: readonly MoneyCents[]): MoneyCents {
  return values.reduce((total, value) => total + value, 0n);
}

export function multiplyMoney(value: MoneyCents, quantity: number): MoneyCents {
  if (!Number.isSafeInteger(quantity) || quantity < 0) {
    throw new Error("Quantity must be a non-negative safe integer.");
  }
  return value * BigInt(quantity);
}

export function percentageOf(
  base: MoneyCents,
  basisPoints: number,
  rounding: "HALF_UP" | "DOWN" = "HALF_UP",
): MoneyCents {
  if (!Number.isSafeInteger(basisPoints) || basisPoints < 0) {
    throw new Error("Basis points must be a non-negative integer.");
  }

  const numerator = base * BigInt(basisPoints);
  const denominator = 10_000n;
  if (rounding === "DOWN") return numerator / denominator;

  const quotient = numerator / denominator;
  const remainder = numerator % denominator;
  return remainder * 2n >= denominator ? quotient + 1n : quotient;
}

export function allocateEvenly(total: MoneyCents, parts: number, remainderIndex = 0): MoneyCents[] {
  if (!Number.isSafeInteger(parts) || parts <= 0) {
    throw new Error("Parts must be a positive safe integer.");
  }
  if (remainderIndex < 0 || remainderIndex >= parts) {
    throw new Error("Invalid remainder index.");
  }

  const divisor = BigInt(parts);
  const base = total / divisor;
  const remainder = total % divisor;
  return Array.from({ length: parts }, (_, index) =>
    index === remainderIndex ? base + remainder : base,
  );
}

export function formatBRL(value: MoneyCents): string {
  const negative = value < 0n;
  const absolute = negative ? -value : value;
  const integer = absolute / 100n;
  const decimal = (absolute % 100n).toString().padStart(2, "0");
  const grouped = integer.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `${negative ? "-" : ""}R$ ${grouped},${decimal}`;
}

export function moneyToJson(value: MoneyCents): string {
  return value.toString();
}

export function moneyFromJson(value: string): MoneyCents {
  return cents(value);
}
